import { convertObjectFieldsToVariables } from '@libraryHelpers/typeAdapters'
import type { Variable } from '@microservices/wiskey-react-components'
import type { ObjectFieldDTO } from '@types'

export function parseExpandsList(expands: string): string[] {
  if (!expands.trim()) return []
  return expands
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .map(part => {
      const colon = part.indexOf(':')
      if (colon === -1) return part.trim()
      return part.slice(0, colon).trim()
    })
    .filter(Boolean)
}

function groupExpandPaths(paths: string[]): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>()
  for (const path of paths) {
    const segments = path.split('.').filter(Boolean)
    if (!segments.length) continue
    const root = segments[0]
    const rest = segments.slice(1).join('.')
    if (!m.has(root)) m.set(root, new Set())
    m.get(root)!.add(rest)
  }
  return m
}

export function collectMissingModelCodesAlongPaths(
  paths: string[],
  rootCode: string,
  modelsMap: Map<string, ObjectFieldDTO[]>
): string[] {
  const missing = new Set<string>()
  const rootFields = modelsMap.get(rootCode)
  if (!rootFields) return []

  for (const path of paths) {
    const segments = path.split('.').filter(Boolean)
    let fields = rootFields
    for (const seg of segments) {
      const f = fields.find(fl => fl.name === seg)
      if (!f?.model) break
      if (!modelsMap.has(f.model)) missing.add(f.model)
      fields = modelsMap.get(f.model) ?? []
    }
  }
  return [...missing]
}

function mergeChildFieldsWithExpands(
  childFields: ObjectFieldDTO[],
  suffixSet: Set<string>,
  modelsMap: Map<string, ObjectFieldDTO[]>
): Variable[] {
  const fullExpand = suffixSet.has('')
  const deepOnly = [...suffixSet].filter(s => s !== '')

  return childFields.map(cf => {
    const needDeep = deepOnly.some(s => s === cf.name || s.startsWith(cf.name + '.'))
    if (fullExpand && !needDeep) {
      return convertObjectFieldsToVariables([cf])[0]
    }
    if (needDeep) {
      const nextSet = new Set<string>()
      for (const s of deepOnly) {
        if (s === cf.name) nextSet.add('')
        else if (s.startsWith(cf.name + '.')) nextSet.add(s.slice(cf.name.length + 1))
      }
      return buildExpandedField(cf, nextSet, modelsMap)
    }
    if (fullExpand) {
      return convertObjectFieldsToVariables([cf])[0]
    }
    return convertObjectFieldsToVariables([cf])[0]
  })
}

function buildExpandedField(
  field: ObjectFieldDTO,
  suffixSet: Set<string>,
  modelsMap: Map<string, ObjectFieldDTO[]>
): Variable {
  if (!field.model || suffixSet.size === 0) {
    return convertObjectFieldsToVariables([field])[0]
  }
  const childFields = modelsMap.get(field.model) ?? []
  if (childFields.length === 0) {
    return convertObjectFieldsToVariables([field])[0]
  }

  const fullExpand = suffixSet.has('')
  const deepOnly = [...suffixSet].filter(s => s !== '')

  if (fullExpand && deepOnly.length === 0) {
    return {
      name: field.name,
      type: convertObjectFieldsToVariables(childFields),
      description: field.desc ?? undefined,
    }
  }
  if (!fullExpand && deepOnly.length > 0) {
    return {
      name: field.name,
      type: mergeChildFieldsWithExpands(childFields, suffixSet, modelsMap),
      description: field.desc ?? undefined,
    }
  }
  if (fullExpand && deepOnly.length > 0) {
    return {
      name: field.name,
      type: mergeChildFieldsWithExpands(childFields, suffixSet, modelsMap),
      description: field.desc ?? undefined,
    }
  }
  return convertObjectFieldsToVariables([field])[0]
}

export function buildItemVariablesFromExpands(
  rootFields: ObjectFieldDTO[],
  expandPaths: string[],
  modelsMap: Map<string, ObjectFieldDTO[]>
): Variable[] {
  if (!expandPaths.length) {
    return convertObjectFieldsToVariables(rootFields)
  }
  const grouped = groupExpandPaths(expandPaths)
  return rootFields.map(field => {
    const suffixes = grouped.get(field.name)
    if (!suffixes || suffixes.size === 0) {
      return convertObjectFieldsToVariables([field])[0]
    }
    return buildExpandedField(field, suffixes, modelsMap)
  })
}
